<div class="space-y-6">
  <p hlmMuted>
    Kennst du ein Geschäft, das Gutscheine verkaufen möchte? Empfiehl Schenkwerk weiter – ihr beide
    zahlt dafür weniger Gebühr.
  </p>

  @if (error(); as message) {
    <div hlmAlert variant="destructive">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>{{ message }}</p>
    </div>
  }

  @if (loading()) {
    <hlm-skeleton class="h-48 w-full rounded-xl" />
    <hlm-skeleton class="h-64 w-full rounded-xl" />
  } @else if (overview(); as o) {
    @if (!o.takes_part) {
      <div hlmAlert>
        <ng-icon name="lucideUsers" />
        <p hlmAlertTitle>Empfehlungen sind für eigene Gutschein-Shops</p>
        <p hlmAlertDescription>
          Netzwerke und ihre Mitglieder haben eigene Konditionen und nehmen nicht teil.
        </p>
      </div>
    } @else {
      <section hlmCard>
        <div hlmCardHeader>
          <h2 hlmCardTitle>Dein Empfehlungslink</h2>
          <p hlmCardDescription>
            Wer sich über diesen Link registriert, zählt als deine Empfehlung. Teile ihn mit
            Geschäften, die du kennst.
          </p>
        </div>
        <div hlmCardContent class="space-y-4">
          <div class="flex flex-col gap-2 sm:flex-row">
            <input
              hlmInput
              readonly
              class="font-mono text-sm sm:flex-1"
              aria-label="Dein Empfehlungslink"
              [value]="link()"
              (focus)="$any($event.target).select()" />
            <button hlmBtn type="button" (click)="copyLink()">
              <ng-icon [name]="copied() ? 'lucideCheck' : 'lucideCopy'" data-icon="inline-start" />
              {{ copied() ? 'Kopiert' : 'Link kopieren' }}
            </button>
          </div>
          <div class="flex flex-wrap gap-2">
            @if (canShare) {
              <button hlmBtn variant="outline" type="button" (click)="share()">
                <ng-icon name="lucideShare2" data-icon="inline-start" />
                Teilen
              </button>
            }
            <a hlmBtn variant="outline" [href]="whatsappUrl()" target="_blank" rel="noopener">
              <ng-icon name="lucideMessageCircle" data-icon="inline-start" />
              WhatsApp
            </a>
            <a hlmBtn variant="outline" [href]="mailtoUrl()">
              <ng-icon name="lucideMail" data-icon="inline-start" />
              E-Mail
            </a>
            <button hlmBtn variant="outline" type="button" (click)="toggleQr()">
              <ng-icon name="lucideQrCode" data-icon="inline-start" />
              {{ showQr() ? 'QR-Code ausblenden' : 'QR-Code' }}
            </button>
          </div>
          @if (showQr()) {
            <div class="flex flex-col items-center gap-3 rounded-xl border p-4 sm:flex-row">
              <div class="shrink-0 bg-white p-2" [innerHTML]="qrSvg()"></div>
              <div class="space-y-2">
                <p class="text-sm">
                  Zum Ausdrucken für den Tresen oder für ein Gespräch vor Ort: Der QR-Code führt
                  direkt zu deinem Empfehlungslink.
                </p>
                <button hlmBtn variant="outline" size="sm" type="button" (click)="downloadQr()">
                  <ng-icon name="lucideDownload" data-icon="inline-start" />
                  QR-Code herunterladen
                </button>
              </div>
            </div>
          }
        </div>
      </section>

      <div class="grid gap-4 sm:grid-cols-3">
        <app-kpi-card
          label="Deine Gebühr jetzt"
          [value]="rate(o.fee_rate)"
          [subtext]="
            o.fee_rate < o.default_fee_rate
              ? 'statt ' + rate(o.default_fee_rate) + ' dank Empfehlungen'
              : 'Standard für neue Verkäufe'
          ">
          <ng-template #icon><ng-icon name="lucidePercent" /></ng-template>
        </app-kpi-card>
        <app-kpi-card
          accent="default"
          label="Empfehlungen"
          [value]="'' + o.referrals.length"
          subtext="Über deinen Link registriert">
          <ng-template #icon><ng-icon name="lucideUsers" /></ng-template>
        </app-kpi-card>
        <app-kpi-card
          accent="default"
          label="Laufende Rabatte"
          [value]="runningCount() + ' von ' + o.max_running_discounts"
          [subtext]="'Je ' + o.discount_points + ' Prozentpunkt weniger Gebühr'">
          <ng-template #icon><ng-icon name="lucideGift" /></ng-template>
        </app-kpi-card>
      </div>

      <section hlmCard>
        <div hlmCardHeader>
          <h2 hlmCardTitle>So funktioniert's</h2>
        </div>
        <div hlmCardContent>
          <ol class="grid gap-4 sm:grid-cols-3">
            <li class="space-y-1">
              <p class="font-medium">1. Link teilen</p>
              <p hlmMuted class="text-sm">
                Ein Geschäft registriert sich über deinen Link und zahlt nach dem Start
                {{ o.welcome_months }} Monate lang nur {{ rate(welcomeRate()) }} statt
                {{ rate(o.default_fee_rate) }} Gebühr.
              </p>
            </li>
            <li class="space-y-1">
              <p class="font-medium">2. Gutscheine verkaufen</p>
              <p hlmMuted class="text-sm">
                Sobald es Gutscheine für {{ qualifyingSales() }} verkauft hat, ist deine Empfehlung
                erfolgreich. Ein Verkauf zählt {{ o.sale_hold_days }} Tage danach, wenn er nicht
                erstattet wurde.
              </p>
            </li>
            <li class="space-y-1">
              <p class="font-medium">3. Weniger Gebühr</p>
              <p hlmMuted class="text-sm">
                Du zahlst {{ o.reward_months }} Monate lang {{ o.discount_points }} Prozentpunkt
                weniger. Bis zu {{ o.max_running_discounts }} Rabatte laufen gleichzeitig, weitere
                starten im Anschluss.
              </p>
            </li>
          </ol>
        </div>
      </section>

      @if (o.discounts.length) {
        <section hlmCard>
          <div hlmCardHeader>
            <h2 hlmCardTitle>Deine Rabatte</h2>
            <p hlmCardDescription>Laufende und kommende Rabatte auf deine Gebühr.</p>
          </div>
          <div hlmCardContent>
            <div hlmTableContainer>
              <table hlmTable>
                <thead hlmTHead>
                  <tr hlmTr>
                    <th hlmTh>Rabatt</th>
                    <th hlmTh>Zeitraum</th>
                    <th hlmTh>Status</th>
                  </tr>
                </thead>
                <tbody hlmTBody>
                  @for (d of o.discounts; track d.starts_on + d.kind + d.referred_name) {
                    <tr hlmTr>
                      <td hlmTd>
                        <span class="font-medium">−{{ d.points }} Prozentpunkt</span>
                        <span hlmMuted class="block text-xs">{{ discountLabel(d) }}</span>
                      </td>
                      <td hlmTd class="tabular-nums">
                        {{ date(d.starts_on) }} – {{ date(d.last_day) }}
                      </td>
                      <td hlmTd>
                        <span hlmBadge [variant]="d.running ? 'solid' : 'outline'">
                          {{ d.running ? 'Läuft' : 'Startet am ' + date(d.starts_on) }}
                        </span>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </section>
      }

      <section hlmCard>
        <div hlmCardHeader>
          <h2 hlmCardTitle>Deine Empfehlungen</h2>
          <p hlmCardDescription>
            Geschäfte, die sich über deinen Link registriert haben. Ihre Umsätze bleiben privat – du
            siehst nur, ob die Empfehlung erfolgreich war.
          </p>
        </div>
        <div hlmCardContent>
          @if (o.referrals.length === 0) {
            <p hlmMuted class="text-sm">Noch hat sich niemand über deinen Link registriert.</p>
          } @else {
            <div hlmTableContainer>
              <table hlmTable>
                <thead hlmTHead>
                  <tr hlmTr>
                    <th hlmTh>Geschäft</th>
                    <th hlmTh>Registriert</th>
                    <th hlmTh>Status</th>
                  </tr>
                </thead>
                <tbody hlmTBody>
                  @for (r of o.referrals; track $index) {
                    <tr hlmTr>
                      <td hlmTd>{{ r.name || 'Noch in der Einrichtung' }}</td>
                      <td hlmTd class="tabular-nums">{{ date(r.registered_on) }}</td>
                      <td hlmTd>
                        <span hlmBadge [variant]="statusVariant(r.status)">
                          {{ statusLabel(r.status) }}
                        </span>
                        @if (r.status === 'rewarded') {
                          <span hlmMuted class="block text-xs">
                            Rabatt {{ date(r.reward_starts_on) }} – {{ date(r.reward_last_day) }}
                          </span>
                        } @else if (r.status === 'selling') {
                          <span hlmMuted class="block text-xs">
                            Noch keine {{ qualifyingSales() }} verkauft
                          </span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </section>
    }
  }
</div>
